import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'
import { createFileRoute } from '@tanstack/react-router'

import type {
    ShopContact,
    ShopContactFields,
    ShopContactInput,
} from '@/lib/api/shop-content-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { ErrorText, TextField } from '@/components/retail/form-bits'
import { setShopServerErrors } from '@/components/retail/shop-errors'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useAuth } from '@/hooks/use-auth'
import {
    useSaveShopContact,
    useShopContact,
} from '@/hooks/use-shop-content-query'
import { ApiError } from '@/lib/api/client'
import { m } from '@/lib/paraglide/messages'

// Bán lẻ › Liên hệ (README 34): một bản ghi dùng chung web (header, footer,
// trang Liên hệ), Mini App (gọi / Zalo / Cần hỗ trợ) và email gửi khách.
export const Route = createFileRoute('/_app/_authed/_sidebar/retail/contact')({
    component: ContactPage,
})

type ContactForm = Record<keyof ShopContactFields, string>

const FIELDS: (keyof ShopContactFields)[] = [
    'site_name',
    'hotline',
    'hotline2',
    'zalo',
    'email',
    'opening_hours',
    'address',
    'tax_info',
    'map_embed_url',
]

// Như server (usecase/shop/home.go SaveContact).
const PHONE_RE = /^\+?[0-9][0-9 .]{6,18}[0-9]$/
const HTTP_RE = /^https?:\/\/\S+$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const LINE_MAX = 120

const line = (v: string) => v.trim().split(/\s+/).filter(Boolean).join(' ')

export function contactToForm(c: ShopContact): ContactForm {
    const out = {} as ContactForm
    for (const f of FIELDS) out[f] = c[f] ?? ''
    return out
}

export function contactToInput(
    version: number,
    f: ContactForm,
): ShopContactInput {
    const out = { version } as ShopContactInput
    for (const k of FIELDS) out[k] = f[k].trim() === '' ? null : f[k]
    return out
}

/** Kiểm nhanh ở FE; lỗi gắn đúng tên ô (trùng params.field server). */
/** Chỉ mã nhúng Google Maps (https://www.google.com/maps/embed?…) — cùng luật server. */
export function isGoogleMapsEmbed(raw: string): boolean {
    if (raw.length > 2000) return false
    try {
        const u = new URL(raw)
        return (
            u.protocol === 'https:' &&
            u.username === '' &&
            u.password === '' &&
            u.host === 'www.google.com' &&
            (u.pathname === '/maps/embed' ||
                u.pathname.startsWith('/maps/embed/')) &&
            u.search.length > 1
        )
    } catch {
        return false
    }
}

export function validateContact(f: ContactForm): Record<string, string> {
    const errors: Record<string, string> = {}
    const hotline = line(f.hotline)
    if (!hotline) errors.hotline = m.error_shop_field_required()
    else if (!PHONE_RE.test(hotline))
        errors.hotline = m.error_shop_phone_invalid()
    const hotline2 = line(f.hotline2)
    if (hotline2 && !PHONE_RE.test(hotline2))
        errors.hotline2 = m.error_shop_phone_invalid()
    const zalo = line(f.zalo)
    if (zalo && !PHONE_RE.test(zalo) && !HTTP_RE.test(zalo))
        errors.zalo = m.error_shop_zalo_invalid()
    const email = f.email.trim()
    if (email && !EMAIL_RE.test(email))
        errors.email = m.error_shop_email_invalid()
    const map = f.map_embed_url.trim()
    if (map && !isGoogleMapsEmbed(map))
        errors.map_embed_url = m.error_shop_map_embed_invalid()
    for (const k of ['site_name', 'opening_hours', 'tax_info'] as const) {
        if ([...line(f[k])].length > LINE_MAX)
            errors[k] = m.error_shop_field_too_long({ max: String(LINE_MAX) })
    }
    if ([...f.address.trim()].length > 300)
        errors.address = m.error_shop_field_too_long({ max: '300' })
    return errors
}

const contactSchema = () =>
    z.custom<ContactForm>().superRefine((f, ctx) => {
        for (const [path, message] of Object.entries(validateContact(f)))
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message })
    })

export function ContactPage() {
    const query = useShopContact()
    const contact = query.data?.data
    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.shop_contact_title()}
                subtitle={m.shop_contact_subtitle()}
            />
            {query.isLoading ? (
                <p className="text-sm text-muted-foreground">
                    {m.common_loading()}
                </p>
            ) : query.isError || !contact ? (
                <p className="text-sm text-destructive" role="alert">
                    {m.shop_contact_load_failed()}
                </p>
            ) : (
                <ContactFormCard
                    key={contact.version}
                    contact={contact}
                    onReload={() => void query.refetch()}
                />
            )}
        </div>
    )
}

function ContactFormCard({
    contact,
    onReload,
}: {
    contact: ShopContact
    onReload: () => void
}) {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('shop_content.write')
    const save = useSaveShopContact()
    const [conflict, setConflict] = useState(false)
    const form = useForm<ContactForm>({
        resolver: zodResolver(contactSchema()),
        defaultValues: contactToForm(contact),
    })

    const submit = form.handleSubmit(async (values) => {
        setConflict(false)
        try {
            await save.mutateAsync(contactToInput(contact.version, values))
        } catch (err) {
            setShopServerErrors(
                form.setError,
                err,
                m.shop_contact_save_failed(),
            )
            setConflict(
                err instanceof ApiError &&
                    err.code === 'SHOP_CONCURRENT_CHANGE',
            )
        }
    })

    return (
        <Card className="max-w-3xl">
            <CardContent className="pt-6">
                <FormProvider {...form}>
                    <form onSubmit={submit} noValidate className="space-y-4">
                        <fieldset
                            disabled={!canWrite}
                            className="grid gap-4 sm:grid-cols-2"
                        >
                            <TextField
                                name="site_name"
                                label={m.shop_contact_site_name()}
                            />
                            <TextField
                                name="hotline"
                                label={`${m.shop_contact_hotline()} *`}
                            />
                            <TextField
                                name="hotline2"
                                label={m.shop_contact_hotline2()}
                            />
                            <TextField
                                name="zalo"
                                label={m.shop_contact_zalo()}
                            />
                            <TextField
                                name="email"
                                label={m.shop_contact_email()}
                            />
                            <TextField
                                name="opening_hours"
                                label={m.shop_contact_opening_hours()}
                            />
                            <div className="sm:col-span-2">
                                <TextField
                                    name="address"
                                    label={m.shop_contact_address()}
                                    rows={2}
                                />
                            </div>
                            <TextField
                                name="tax_info"
                                label={m.shop_contact_tax_info()}
                            />
                            <TextField
                                name="map_embed_url"
                                label={m.shop_contact_map_embed_url()}
                                hint={m.shop_contact_map_embed_hint()}
                            />
                        </fieldset>
                        <p className="text-xs text-muted-foreground">
                            {m.shop_contact_note()}
                        </p>
                        <ErrorText
                            text={form.formState.errors.root?.server?.message}
                        />
                        {conflict && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={onReload}
                            >
                                {m.shop_home_reload()}
                            </Button>
                        )}
                        {canWrite && (
                            <div className="flex justify-end">
                                <Button type="submit" disabled={save.isPending}>
                                    {save.isPending
                                        ? m.common_saving()
                                        : m.common_save()}
                                </Button>
                            </div>
                        )}
                    </form>
                </FormProvider>
            </CardContent>
        </Card>
    )
}
